import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import { useLanguage } from '../../i18n/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import imgDivider from '../../../assets/header/divider.png';
import imgLogo from '../../../assets/header/logo.png';
import cvPdf from '../../../assets/header/HojaVida_ProfesionalSoftware.pdf';
import imgLogoDark from '../../../assets/header/logoDark.png';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    closeMenu();
  };

  return (
    <header className="header" data-name="Header" data-node-id="7:738">
      <div className="header__container" data-name="Container" data-node-id="7:739">
        {/* Logo */}
        <button 
          className="header__logo" 
          onClick={handleLogoClick}
          aria-label="Ir al inicio"
          data-node-id="7:740"
        >
          <img
            src={theme === 'dark' ? imgLogoDark : imgLogo}
            alt="Logo"
            className="header__logo-img"
          />
        </button>

        {/* Navigation - Desktop */}
        <nav className="header__nav header__nav--desktop" data-name="Navigation" data-node-id="7:741">
          <button onClick={() => handleNavClick('about')} className="header__nav-link" data-node-id="7:742">
            {t('header.aboutBtn')}
          </button>
          <button onClick={() => handleNavClick('experience')} className="header__nav-link" data-node-id="7:743">
            {t('header.experienceBtn')}
          </button>
          <button onClick={() => handleNavClick('projects')} className="header__nav-link" data-node-id="7:744">
            {t('header.projectsBtn')}
          </button>
          <button onClick={() => handleNavClick('contact')} className="header__nav-link" data-node-id="7:745">
            {t('header.contactBtn')}
          </button>

          {/* Divider */}
          <div className="header__divider">
            <img src={imgDivider} alt="divider" className="header__divider-img" data-node-id="7:746" />
          </div>

          {/* Actions */}
          <div className="header__actions" data-name="Actions" data-node-id="7:747">
            {/* Language Toggle */}
            <div className="header__language" data-name="idioma" data-node-id="7:703">
              <button
                className={`header__language-btn ${language === 'es' ? 'header__language-btn--active' : ''}`}
                onClick={() => setLanguage('es')}
                data-node-id="7:700"
              >
                ES
              </button>
              <button
                className={`header__language-btn ${language === 'en' ? 'header__language-btn--active' : ''}`}
                onClick={() => setLanguage('en')}
                data-node-id="7:704"
              >
                EN
              </button>
            </div>

            {/* Theme Toggle */}
            <button
              className="header__theme-btn"
              onClick={toggleTheme}
              aria-label={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
            >
              {theme === 'light' ? (
                <svg className="header__theme-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg className="header__theme-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              )}
            </button>

            {/* Download CV Button */}
            <a
              href={cvPdf}
              download="HojaVida_ProfesionalSoftware.pdf"
              className="header__cv-btn"
              data-node-id="7:749"
            >
              {t('header.downloadCV')}
            </a>
          </div>
        </nav>

        {/* Language Mobile */}
        <div className="header__language header__language--mobile" data-name="idioma-mobile">
          <button
            className={`header__language-btn ${language === 'es' ? 'header__language-btn--active' : ''}`}
            onClick={() => setLanguage('es')}
          >
            ES
          </button>
          <button
            className={`header__language-btn ${language === 'en' ? 'header__language-btn--active' : ''}`}
            onClick={() => setLanguage('en')}
          >
            EN
          </button>
        </div>

        {/* Theme Toggle Mobile */}
        <button
          className="header__theme-btn header__theme-btn--mobile"
          onClick={toggleTheme}
          aria-label={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
        >
          {theme === 'light' ? (
            <svg className="header__theme-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg className="header__theme-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
        </button>

        {/* Mobile Menu Button */}
        <button className="header__menu-btn" aria-label="Toggle menu" onClick={() => setIsMenuOpen(true)}>
          <svg className="header__menu-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 6H20" strokeWidth="2" strokeLinecap="round" />
            <path d="M4 12H20" strokeWidth="2" strokeLinecap="round" />
            <path d="M4 18H20" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {isMenuOpen && (
        <div className="header__mobile-overlay" onClick={closeMenu}>
          <div className="header__mobile-panel" onClick={(e) => e.stopPropagation()}>
            <div className="header__mobile-top">
              <button className="header__logo" onClick={handleLogoClick} aria-label="Ir al inicio">
                <img src={imgLogo} alt="Logo" className="header__logo-img" />
              </button>
              <button className="header__mobile-close" aria-label="Cerrar menú" onClick={closeMenu}>
                <svg className="header__mobile-close-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6L18 18" strokeWidth="2" strokeLinecap="round" />
                  <path d="M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="header__mobile-links">
              <button onClick={() => handleNavClick('about')} className="header__mobile-link">
                {t('header.aboutBtn')}
              </button>
              <button onClick={() => handleNavClick('experience')} className="header__mobile-link">
                {t('header.experienceBtn')}
              </button>
              <button onClick={() => handleNavClick('projects')} className="header__mobile-link">
                {t('header.projectsBtn')}
              </button>
              <button onClick={() => handleNavClick('contact')} className="header__mobile-link">
                {t('header.contactBtn')}
              </button>
            </div>

            <a
              href={cvPdf}
              download="HojaVida_ProfesionalSoftware.pdf"
              className="header__cv-btn"
              data-node-id="7:749"
              onClick={closeMenu}
            >
              {t('header.downloadCV')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
